import { Formik, Form, useFormikContext, useField, ErrorMessage, type FormikHelpers } from "@/shared/lib/formik-compat";
import ky, { type KyInstance } from "ky";
import { useAuthStore } from "@/shared/stores/authStore";
import { toast } from "@/shared/stores/notificationStore";
import { handleCUAError } from "@/shared/lib/api/cuaErrorHandlers";

export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "/api";

/**
 * ky HTTP client — replaces SWR fetcher + native fetch pattern.
 *
 * - Auto-injects Bearer token from authStore
 * - Auto-logout on 401
 * - Retry on 5xx with backoff (GET only)
 * - 30s timeout
 */
export const api: KyInstance = ky.create({
  prefixUrl: API_BASE,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
  retry: {
    limit: 2,
    statusCodes: [500, 502, 503, 504],
    methods: ["get", "head", "options"],
    backoffLimit: 3000,
  },
  hooks: {
    beforeRequest: [
      (request) => {
        const url = new URL(request.url);
        // Guard: strip duplicate /api/api/ prefix caused by api.get('v1/...')
        if (url.pathname.startsWith('/api/api/')) {
          const fixed = url.pathname.replace('/api/api/', '/api/');
          return new Request(
            request.url.replace(url.pathname, fixed),
            {
              method: request.method,
              headers: request.headers,
              body: request.body,
              signal: request.signal,
              credentials: request.credentials,
            }
          );
        }
      },
      (request) => {
        const token = useAuthStore.getState().token;
        if (token) request.headers.set("Authorization", `Bearer ${token}`);
      },
    ],
    afterResponse: [
      async (_req, _opts, response) => {
        if (response.status === 401) {
          useAuthStore.getState().logout();
          const currentUrl = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/auth/login?next=${currentUrl}`;
        }
        return response;
      },
    ],
    beforeError: [
      async (error) => {
        if (error.response) {
          try {
            const body = await error.response.clone().json() as { detail?: string; code?: string; details?: Record<string, unknown> };
            // CU-005-2: handle CUA-specific errors first; returns true if handled
            const wasCUAError = handleCUAError(error.response.status, body);
            if (!wasCUAError && body.detail) error.message = body.detail;
          } catch { /* not JSON */ }
        }
        return error;
      },
    ],
  },
});

export { api as apiClient };

export const CHAT_API_BASE =
  import.meta.env.VITE_CHAT_API_URL ?? import.meta.env.VITE_API_URL ?? API_BASE;

export const chatApi: KyInstance = api.extend({
  prefixUrl: CHAT_API_BASE,
});

/** For file uploads — no Content-Type header (browser sets boundary) */
export const apiMultipart: KyInstance = api.extend({
  headers: { "Content-Type": undefined },
});

export async function getErrorMessage(error: unknown): Promise<string> {
  if (error instanceof Error) {
    if ("response" in error && error.response instanceof Response) {
      try {
        const body = await (error.response as Response).clone().json() as { detail?: string };
        if (body.detail) return body.detail;
      } catch { /* not JSON */ }
    }
    return error.message;
  }
  return "An unexpected error occurred";
}

export async function withErrorToast<T>(fn: () => Promise<T>, errorTitle = "Error"): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    const message = await getErrorMessage(error);
    toast.error(errorTitle, message);
    throw error;
  }
}
